import {
  NS,
  type AuditActorKind,
  type AuditEventV1,
  type NamespaceTuple,
  findAuditEventPrivacyViolations,
  validateAuditEventV1,
} from '@ownyou/shared-types';
import type { AuditKeyStore } from './audit-signing';
import { verifyAuditEvent } from './audit-signing';

export type AuditLogVerificationState =
  | 'verified'
  | 'invalid_signature'
  | 'missing_delegation'
  | 'privacy_failure'
  | 'schema_invalid';

export interface AuditLogQuery {
  cursor?: string | null;
  limit?: number;
  since?: number;
  until?: number;
  actor?: AuditActorKind;
  action?: string;
  targetKind?: string;
  deviceId?: string;
}

export interface AuditLogRow {
  id: string;
  ts: number;
  actor: AuditEventV1['actor'];
  action: string;
  target: AuditEventV1['target'];
  result?: AuditEventV1['result'];
  reason?: string;
  verificationState: AuditLogVerificationState;
  verificationErrors: string[];
  deviceId: string;
  auditKeyId: string;
  delegationId: string;
  traceId?: string;
  routeId?: string;
}

export interface AuditLogReadModel {
  rows: AuditLogRow[];
  nextCursor: string | null;
  totalMatched: number;
  sourceReadCount: number;
  sourceTruncated: boolean;
  sourceReadLimit: number;
  sourceNamespace: NamespaceTuple;
  scope: 'current-device';
}

export interface AuditLogStoreReader {
  list<T>(namespace: NamespaceTuple, options?: { limit?: number; offset?: number }): Promise<{ items: T[] }>;
}

const SOURCE_PAGE_SIZE = 1000;
const SOURCE_READ_LIMIT = 100_000;

export async function readAuditLog(params: {
  store: AuditLogStoreReader;
  userId: string;
  keyStore: AuditKeyStore;
  currentDeviceId: string;
  query?: AuditLogQuery;
}): Promise<AuditLogReadModel> {
  const namespace = NS.auditEvents(params.userId);
  const query = params.query ?? {};
  const limit = clampLimit(query.limit);
  const offset = parseCursor(query.cursor);
  const targetDeviceId = query.deviceId ?? params.currentDeviceId;
  const listed = await listAuditEvents(params.store, namespace);

  const filtered = listed.items
    .filter((event) => event.deviceId === targetDeviceId)
    .filter((event) => query.since === undefined || event.ts >= query.since)
    .filter((event) => query.until === undefined || event.ts <= query.until)
    .filter((event) => query.actor === undefined || event.actor.kind === query.actor)
    .filter((event) => query.action === undefined || event.action === query.action)
    .filter((event) => query.targetKind === undefined || event.target.kind === query.targetKind)
    .sort((left, right) => right.ts - left.ts || right.id.localeCompare(left.id));

  const page = filtered.slice(offset, offset + limit);
  const rows = await Promise.all(page.map((event) => toAuditLogRow(event, params.keyStore)));
  const nextOffset = offset + page.length;

  return {
    rows,
    nextCursor: nextOffset < filtered.length ? String(nextOffset) : null,
    totalMatched: filtered.length,
    sourceReadCount: listed.items.length,
    sourceTruncated: listed.truncated,
    sourceReadLimit: SOURCE_READ_LIMIT,
    sourceNamespace: namespace,
    scope: 'current-device',
  };
}

async function listAuditEvents(
  store: AuditLogStoreReader,
  namespace: NamespaceTuple,
): Promise<{ items: AuditEventV1[]; truncated: boolean }> {
  const items: AuditEventV1[] = [];
  let offset = 0;

  while (items.length < SOURCE_READ_LIMIT) {
    const page = await store.list<AuditEventV1>(namespace, {
      limit: Math.min(SOURCE_PAGE_SIZE, SOURCE_READ_LIMIT - items.length),
      offset,
    });
    items.push(...page.items);
    offset += page.items.length;

    if (page.items.length < SOURCE_PAGE_SIZE) {
      return { items, truncated: false };
    }
  }

  return { items, truncated: true };
}

async function toAuditLogRow(event: AuditEventV1, keyStore: AuditKeyStore): Promise<AuditLogRow> {
  const validation = validateAuditEventV1(event);
  const privacyViolations = findAuditEventPrivacyViolations(event);
  let verificationState: AuditLogVerificationState = 'verified';
  let verificationErrors: string[] = [];

  if (privacyViolations.length > 0) {
    verificationState = 'privacy_failure';
    verificationErrors = privacyViolations.map((violation) => `${violation.path}: ${violation.rule}`);
  } else if (!validation.ok) {
    verificationState = 'schema_invalid';
    verificationErrors = [
      ...validation.errors,
      ...validation.privacyViolations.map((violation) => `${violation.path}: ${violation.rule}`),
    ];
  } else {
    const key = await keyStore.loadByDelegation(event.userId, event.deviceId, event.delegationId);
    if (!key) {
      verificationState = 'missing_delegation';
      verificationErrors = ['Audit key delegation metadata unavailable on this device'];
    } else {
      const verified = await verifyAuditEvent(event, key.delegation);
      if (!verified.ok) {
        verificationState = 'invalid_signature';
        verificationErrors = verified.errors;
      }
    }
  }

  return {
    id: event.id,
    ts: event.ts,
    actor: event.actor,
    action: event.action,
    target: event.target,
    result: event.result,
    reason: event.reason,
    verificationState,
    verificationErrors,
    deviceId: event.deviceId,
    auditKeyId: event.auditKeyId,
    delegationId: event.delegationId,
    traceId: event.traceId,
    routeId: event.routeId,
  };
}

function clampLimit(limit: number | undefined): number {
  if (limit === undefined || !Number.isFinite(limit)) return 25;
  return Math.min(100, Math.max(1, Math.floor(limit)));
}

function parseCursor(cursor: string | null | undefined): number {
  if (!cursor) return 0;
  const parsed = Number(cursor);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
}
